import { createFocusTrap, FocusTrap } from 'focus-trap';

const hamburgerActiveClass = 'is-active';

type HamburgerMenuInit = {
    hamburger: HTMLButtonElement,
    menu: HTMLElement,
    container: HTMLElement,
};

export default class HamburgerMenu {
    private readonly hamburger: HTMLButtonElement;
    private readonly menu: HTMLElement;
    private readonly focusTrap: FocusTrap;

    constructor({ hamburger, menu, container }: HamburgerMenuInit) {
        this.hamburger = hamburger;
        this.menu = menu;
        this.focusTrap = createFocusTrap(container, {
            initialFocus: menu,
        });

        this.toggle = this.toggle.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);

        this.init();
    }

    init() {
        this.hamburger.setAttribute('aria-controls', this.menu.id);
        this.hamburger.setAttribute('aria-expanded', 'false');
        this.hamburger.addEventListener('click', this.toggle);
    }

    destroy() {
        this.hamburger.removeAttribute('aria-controls');
        this.hamburger.removeAttribute('aria-expanded');
        this.hamburger.removeEventListener('click', this.toggle);
    }

    show() {
        this.menu.removeAttribute('aria-hidden');
        this.hamburger.classList.add(hamburgerActiveClass);
        this.hamburger.setAttribute('aria-expanded', 'true');
        document.body.addEventListener('keydown', this.handleKeydown);
        this.focusTrap.activate();
    }

    hide() {
        this.menu.setAttribute('aria-hidden', 'true');
        this.hamburger.classList.remove(hamburgerActiveClass);
        this.hamburger.setAttribute('aria-expanded', 'false');
        document.body.removeEventListener('keydown', this.handleKeydown);
        this.focusTrap.deactivate();
    }

    toggle() {
        if (this.shown) {
            this.hide();
        } else {
            this.show();
        }
    }

    handleKeydown({ key }: KeyboardEvent) {
        if (key === 'Esc' || key === 'Escape') {
            this.hide();
        }
    }

    get shown() {
        return this.hamburger.getAttribute('aria-expanded') === 'true';
    }

    static createFromSelectors(hamburgerSelector: string, menuSelector: string, containerSelector: string) {
        const hamburger = document.querySelector(hamburgerSelector);
        const menu = document.querySelector(menuSelector);
        const container = document.querySelector(containerSelector);

        if (!(hamburger instanceof HTMLButtonElement)) {
            throw new Error(`Unable to find the hamburger element "${hamburgerSelector}".`);
        }

        if (!(menu instanceof HTMLElement)) {
            throw new Error(`Unable to find the menu element "${menuSelector}".`);
        }

        if (!(container instanceof HTMLElement)) {
            throw new Error(`Unable to find the container element "${containerSelector}".`);
        }

        return new HamburgerMenu({ hamburger, menu, container });
    }
}
